/**
 * 切歌与播放模式：fadeOutBeforeSwitch、handlePrevious、handleNext、togglePlayMode
 * 抽离自 MusicPlayer.js
 */
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { runFadeOut } from '../fadeUtils';
import { RANDOM_RECENT_DEDUP_N } from '../constants';

/**
 * @param {object} options
 * @param {Array} options.playlist
 * @param {object|null} options.currentMusic
 * @param {string} options.playMode
 * @param {function(object): void} options.onNext
 * @param {function(object): void} options.onPrevious
 * @param {boolean} options.fadeInOut
 * @param {function(object): string|null} options.getVideoFilePath
 * @param {function(boolean, React.RefObject, React.RefObject): HTMLMediaElement|null} options.getActiveMediaElement
 * @param {React.RefObject} options.videoRef
 * @param {React.RefObject} options.audioRef
 * @param {boolean} options.playing
 * @param {boolean} options.muted
 * @param {React.MutableRefObject} options.fadeOutTimerRef
 * @param {function(boolean): void} options.setIsVisualSwitching
 * @param {function(function(number): number): void} options.setVisualFadeOutToken
 * @param {function(string): void} options.setPlayMode
 * @returns {{ fadeOutBeforeSwitch: function, handlePrevious: function, handleNext: function, togglePlayMode: function }}
 */
export function usePlayerPlaylistHandlers({
  playlist,
  currentMusic,
  playMode,
  onNext,
  onPrevious,
  fadeInOut,
  getVideoFilePath,
  getActiveMediaElement,
  videoRef,
  audioRef,
  playing,
  muted,
  fadeOutTimerRef,
  setIsVisualSwitching,
  setVisualFadeOutToken,
  setPlayMode
}) {
  const playlistIds = useMemo(() => {
    try {
      return (playlist || []).map(m => m?.id).filter(Boolean);
    } catch (_) {
      return [];
    }
  }, [playlist]);

  const idToMusic = useMemo(() => {
    const map = new Map();
    try {
      for (const m of (playlist || [])) {
        if (m && m.id != null) map.set(m.id, m);
      }
    } catch (_) {}
    return map;
  }, [playlist]);

  const randomStateRef = useRef({
    playlistKey: '',
    unplayed: [],
    recent: [],
    history: [],
    historyCursor: -1
  });

  const clampRecentN = useCallback((len) => {
    const L = Number(len) || 0;
    if (L <= 1) return 0;
    return Math.max(0, Math.min(RANDOM_RECENT_DEDUP_N, L - 1));
  }, []);

  const resetRandomCycleIfNeeded = useCallback(() => {
    const st = randomStateRef.current;
    const key = playlistIds.join(',');
    if (st.playlistKey === key) return;
    st.playlistKey = key;

    // 过滤 history/recent 中已不存在的曲目
    const idSet = new Set(playlistIds);
    st.recent = (st.recent || []).filter(id => idSet.has(id));
    st.history = (st.history || []).filter(id => idSet.has(id));
    st.historyCursor = Math.min(st.historyCursor, st.history.length - 1);

    // 新播放列表：重置 unplayed（开始新一轮）
    st.unplayed = [...playlistIds];
  }, [playlistIds]);

  const markPlayed = useCallback((id) => {
    if (id == null) return;
    const st = randomStateRef.current;

    // 从 unplayed 移除（幂等）
    if (st.unplayed && st.unplayed.length) {
      const idx = st.unplayed.indexOf(id);
      if (idx >= 0) st.unplayed.splice(idx, 1);
    }

    // recent queue（去重后 push，保留最近 N）
    const n = clampRecentN(playlistIds.length);
    if (n > 0) {
      st.recent = (st.recent || []).filter(x => x !== id);
      st.recent.push(id);
      if (st.recent.length > n) st.recent.splice(0, st.recent.length - n);
    } else {
      st.recent = [];
    }

    // history：若用户之前按“上一首”回退过，新的播放应截断未来分支
    if (st.historyCursor >= 0 && st.historyCursor < (st.history.length - 1)) {
      st.history = st.history.slice(0, st.historyCursor + 1);
    }
    if (!st.history.length || st.history[st.history.length - 1] !== id) {
      st.history.push(id);
    }
    st.historyCursor = st.history.length - 1;
  }, [playlistIds.length, clampRecentN]);

  // playlist 变化时：重置/对齐随机状态
  useEffect(() => {
    resetRandomCycleIfNeeded();
  }, [resetRandomCycleIfNeeded]);

  // 当前曲目变化时：把它标记为“已播放”（用于 random 的一轮去重）
  useEffect(() => {
    if (!currentMusic?.id) return;
    resetRandomCycleIfNeeded();
    markPlayed(currentMusic.id);
  }, [currentMusic?.id, resetRandomCycleIfNeeded, markPlayed]);

  const pickRandomId = useCallback((ids) => {
    if (!ids || ids.length === 0) return null;
    const idx = Math.floor(Math.random() * ids.length);
    return ids[idx] ?? null;
  }, []);

  const chooseNextRandomMusic = useCallback(() => {
    const st = randomStateRef.current;
    const currentId = currentMusic?.id ?? null;
    const idSet = new Set(playlistIds);

    // 若 unplayed 空：一轮播完，重置开始下一轮（但保留 recent，用于跨轮去重）
    if (!st.unplayed || st.unplayed.length === 0) {
      st.unplayed = [...playlistIds];
    } else {
      // 清理可能残留的无效 id
      st.unplayed = st.unplayed.filter(id => idSet.has(id));
      if (st.unplayed.length === 0) st.unplayed = [...playlistIds];
    }

    const recentSet = new Set(st.recent || []);

    // 1) 严格：未播放 && 不在 recent && 不是当前
    let candidates = st.unplayed.filter(id => id !== currentId && !recentSet.has(id));
    // 2) 放宽：未播放 && 不是当前（忽略 recent）
    if (candidates.length === 0) {
      candidates = st.unplayed.filter(id => id !== currentId);
    }
    // 3) 兜底：未播放（可能只有 1 首）
    if (candidates.length === 0) {
      candidates = [...st.unplayed];
    }

    const pickedId = pickRandomId(candidates);
    const picked = pickedId != null ? idToMusic.get(pickedId) : null;
    return picked || null;
  }, [currentMusic?.id, playlistIds, idToMusic, pickRandomId]);

  const fadeOutBeforeSwitch = useCallback((switchFn, options = {}) => {
    const immediate = options?.immediate === true;
    if (immediate || !fadeInOut || !switchFn) {
      if (switchFn) switchFn();
      return;
    }
    const hasVideo = currentMusic ? !!getVideoFilePath(currentMusic) : false;
    const mediaElement = getActiveMediaElement(hasVideo, videoRef, audioRef);
    if (!mediaElement || !playing || muted) {
      switchFn();
      return;
    }
    if (fadeOutTimerRef.current) {
      clearInterval(fadeOutTimerRef.current);
      fadeOutTimerRef.current = null;
      switchFn();
      return;
    }
    const isAtEnd = mediaElement.ended || (Number.isFinite(mediaElement.duration) && mediaElement.duration > 0 && mediaElement.currentTime >= mediaElement.duration - 0.05);
    if (isAtEnd) {
      switchFn();
      return;
    }
    setIsVisualSwitching(true);
    setVisualFadeOutToken((v) => v + 1);
    fadeOutTimerRef.current = runFadeOut(mediaElement, () => {
      fadeOutTimerRef.current = null;
      switchFn();
    });
  }, [fadeInOut, currentMusic, muted, playing, getVideoFilePath, getActiveMediaElement, videoRef, audioRef, fadeOutTimerRef, setIsVisualSwitching, setVisualFadeOutToken]);

  const handlePrevious = useCallback((options = {}) => {
    if (!playlist || playlist.length === 0) return;
    if (playMode === 'random') {
      // random 模式：优先按历史回退（更符合“上一首”直觉）
      const st = randomStateRef.current;
      if (st.history && st.history.length >= 2 && st.historyCursor > 0) {
        const prevId = st.history[st.historyCursor - 1];
        const prevMusic = prevId != null ? idToMusic.get(prevId) : null;
        if (prevMusic && onPrevious) {
          st.historyCursor = st.historyCursor - 1;
          fadeOutBeforeSwitch(() => onPrevious(prevMusic), options);
          return;
        }
      }
      // 没有历史：退化为随机挑一个（仍走 onPrevious 以保持语义）
      const picked = chooseNextRandomMusic();
      if (picked && onPrevious) {
        fadeOutBeforeSwitch(() => onPrevious(picked), options);
      }
      return;
    }
    const currentIndex = playlist.findIndex(m => m.id === currentMusic?.id);
    const nextIndex = currentIndex > 0 ? currentIndex - 1 : playlist.length - 1;
    if (onPrevious) {
      fadeOutBeforeSwitch(() => onPrevious(playlist[nextIndex]), options);
    }
  }, [playlist, currentMusic, playMode, onPrevious, fadeOutBeforeSwitch, idToMusic, chooseNextRandomMusic]);

  const handleNext = useCallback((options = {}) => {
    if (!playlist || playlist.length === 0) return;
    if (playMode === 'random') {
      const nextMusic = chooseNextRandomMusic();
      if (nextMusic && onNext) {
        // 先在本地状态里“预标记”一次，避免连续点击 next 时重复选中同一首
        markPlayed(nextMusic.id);
        fadeOutBeforeSwitch(() => onNext(nextMusic), options);
      }
      return;
    }
    const currentIndex = playlist.findIndex(m => m.id === currentMusic?.id);
    let nextIndex;
    if (playMode === 'repeat') nextIndex = currentIndex;
    else nextIndex = currentIndex < playlist.length - 1 ? currentIndex + 1 : 0;
    if (onNext) {
      fadeOutBeforeSwitch(() => onNext(playlist[nextIndex]), options);
    }
  }, [playlist, currentMusic, playMode, onNext, fadeOutBeforeSwitch, chooseNextRandomMusic, markPlayed]);

  const togglePlayMode = useCallback(() => {
    const modes = ['sequential', 'random', 'repeat'];
    const currentIndex = modes.indexOf(playMode);
    const nextIndex = (currentIndex + 1) % modes.length;
    setPlayMode(modes[nextIndex]);
  }, [playMode, setPlayMode]);

  return { fadeOutBeforeSwitch, handlePrevious, handleNext, togglePlayMode };
}
